import React, { useState } from 'react';
import { Send, Mail, MessageSquare, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', message: '' });
      setIsSubmitting(false);
    }, 1500);
  };

  return (
    <section id="contact" className="relative py-24 bg-gunmetal-950">
      {/* Background */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-5"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/1472237/pexels-photo-1472237.jpeg)'
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-4">
            <span className="text-platinum animate-chrome-pulse">START YOUR </span>
            <span className="text-accent-red">TRANSFORMATION</span>
          </h2>
          <div className="w-24 h-1 bg-accent-red mx-auto mb-6"></div>
          <p className="text-xl text-steel-400 max-w-3xl mx-auto">
            Ready to redesign your life? Get in touch and join the MINDESIGN community.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left - Contact Form */}
          <div className="bg-gunmetal-900/50 backdrop-blur-sm rounded-lg p-8 border border-steel-700/30">
            <h3 className="text-2xl font-bold text-steel-100 mb-6">Send Us a Message</h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Field */}
              <div>
                <label htmlFor="name" className="block text-steel-300 font-semibold mb-2">
                  Your Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-steel-500" size={20} />
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your name"
                    className="pl-12 bg-gunmetal-800 border-steel-700 text-steel-100 placeholder:text-steel-600 focus:border-accent-red"
                  />
                </div>
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-steel-300 font-semibold mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-steel-500" size={20} />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                    className="pl-12 bg-gunmetal-800 border-steel-700 text-steel-100 placeholder:text-steel-600 focus:border-accent-red"
                  />
                </div>
              </div>

              {/* Message Field */}
              <div>
                <label htmlFor="message" className="block text-steel-300 font-semibold mb-2">
                  Your Message
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 text-steel-500" size={20} />
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Tell us about your journey..."
                    rows={6}
                    className="pl-12 bg-gunmetal-800 border-steel-700 text-steel-100 placeholder:text-steel-600 focus:border-accent-red resize-none"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-accent-red hover:bg-accent-red-dark text-white font-bold py-6 text-lg transition-all duration-300 hover:shadow-lg hover:shadow-accent-red/40 group"
              >
                {isSubmitting ? (
                  <span>Sending...</span>
                ) : (
                  <>
                    Send Message
                    <Send className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={20} />
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Right - Info & CTA */}
          <div className="space-y-8">
            {/* Newsletter Signup */}
            <div className="bg-gradient-to-br from-accent-red/10 to-accent-red/5 backdrop-blur-sm rounded-lg p-8 border border-accent-red/30">
              <h3 className="text-2xl font-bold text-steel-100 mb-4">
                Join the MINDESIGN Community
              </h3>
              <p className="text-steel-400 mb-6">
                Subscribe to receive exclusive content, new releases, and transformative insights 
                delivered straight to your inbox.
              </p>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  className="bg-gunmetal-900 border-steel-700 text-steel-100 placeholder:text-steel-600"
                />
                <Button className="bg-accent-red hover:bg-accent-red-dark text-white font-semibold px-6">
                  Subscribe
                </Button>
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-gunmetal-900/50 backdrop-blur-sm rounded-lg p-8 border border-steel-700/30">
              <h3 className="text-xl font-bold text-steel-100 mb-6">Connect With Us</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-accent-red/10 rounded-lg flex items-center justify-center">
                    <Mail className="text-accent-red" size={20} />
                  </div>
                  <div>
                    <p className="text-steel-500 text-sm">Email</p>
                    <p className="text-steel-200 font-semibold">info@mindesignenterprises.com</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Mission Statement */}
            <div className="bg-gunmetal-950/80 backdrop-blur-sm rounded-lg p-8 border-l-4 border-accent-red">
              <p className="text-xl font-bold text-steel-200 italic leading-relaxed">
                "At MINDESIGN, we believe that every individual has the power to redesign 
                their life narrative. Your transformation starts here."
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
